import { useMatomo } from '@datapunt/matomo-tracker-react';
import React, { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';

import AppStorePng from '@assets/images/appstore.png';
import GooglePlayPng from '@assets/images/googleplay.png';
import QRAndroidPng from '@assets/images/qr-android.png';
import QRIOSPng from '@assets/images/qr-ios.png';
import { ReactComponent as GuideDarkIcon } from '@assets/svg/help-and-resources/ic-guide-dark.svg';
import { ReactComponent as GuideQuestionDarkIcon } from '@assets/svg/help-and-resources/ic-guide-question-dark.svg';
import { ReactComponent as GuideQuestionIcon } from '@assets/svg/help-and-resources/ic-guide-question.svg';
import { ReactComponent as GuideIcon } from '@assets/svg/help-and-resources/ic-guide.svg';
import { ReactComponent as OnboardingDarkIcon } from '@assets/svg/help-and-resources/ic-onboarding-dark.svg';
import { ReactComponent as OnboardingIcon } from '@assets/svg/help-and-resources/ic-onboarding.svg';
import { ReactComponent as WarrantyDarkIcon } from '@assets/svg/help-and-resources/ic-warranty-dark.svg';
import { ReactComponent as WarrantyIcon } from '@assets/svg/help-and-resources/ic-warranty.svg';

import Header from '@components/common/Header/Header';
import Button from '@components/uikit/Button/Button';

import { MODAL_NAMES } from '@constants/modalRenderers';

import useTranslation from '@hooks/useTranslation';

import { useUserProfileQuery } from '@services/user/useUserProfileQuery';

import { selectAppPersistDarkMode, useAppPersistActions } from '@store/useAppStorePersist';
import { useModalActions } from '@store/useModalStore';

import AppCard from './components/AppCard/AppCard';
import OtherCard from './components/OtherCard/OtherCard';
import TeamMemberCard from './components/TeamMemberCard/TeamMemberCard';
import type { IApp, IOther } from './model/items';
import { useSupportContactsQuery } from './services/useSupportContactsQuery';
import { useUserGuideListQuery } from './services/useUserGuideListQuery';
import style from './style.module.scss';

const defaultUrl =
  'https://v2-api.waites.net/file-manager/open/E6MDp7fX0=kVQGxy5J6qQjQnIsvx7NnzWERCbbF71esrQhdPpLCZCMwT15O1D-k-9zOxfNdQwjLKFJhLdfFCei22LNwX-T24t1qxLODKbu2Uh7O22tze8RT221-89245T1epqNp-E$l0cKXz5NUUYFDhD0wVwixnGIx0TqMtV2dBO2mhR9PBdlksNaIr4ui9UiXwuWQxHhHydILXDbG2a17k47$n9wq9jUHR0=';

const appStoreLink = 'https://apps.apple.com/ua/app/waites-full-service-dashboard/id1658615083';
const googlePlayLink = 'https://play.google.com/store/apps/details?id=com.waites.fullservice';

const HelpAndResources = () => {
  const { trackPageView } = useMatomo();
  const navigate = useNavigate();
  const { customerId } = useParams();
  const { t } = useTranslation();

  const { setAppPersistOnBoardStart } = useAppPersistActions();
  const { setActiveModal } = useModalActions();

  const { data: profileData } = useUserProfileQuery();
  const { data } = useUserGuideListQuery();

  const { data: supportTeamData } = useSupportContactsQuery(customerId);

  useEffect(() => {
    trackPageView({});
  }, []);

  const relaunchOnboarding = () => {
    setAppPersistOnBoardStart(true);
  };

  const darkMode = selectAppPersistDarkMode();

  const apps: Array<IApp> = [
    {
      link: t('Download'),
      linkURL: appStoreLink,
      qrCodeImage: QRIOSPng,
      storeImage: AppStorePng,
      title: t('Install our Full-Service Dashboard mobile app on your iOS device.'),
    },
    {
      link: t('Download'),
      linkURL: googlePlayLink,
      qrCodeImage: QRAndroidPng,
      storeImage: GooglePlayPng,
      title: t('Install our Full-Service Dashboard mobile app on your Android device.'),
    },
  ];

  const others: Array<IOther> = [
    {
      buttonText: t('Open guide'),
      onClick: () => {
        const newWindow = window.open(
          (data?.base_guide?.[profileData?.user?.language_iso_code] || data?.base_guide?.['en'])?.url ||
            defaultUrl,
          '_blank',
        );

        if (newWindow) {
          newWindow.focus();
        }
      },
      icon: darkMode ? <GuideDarkIcon /> : <GuideIcon />,
      title: t('Waites Full-Service Dashboard'),
      description: t(
        'The Waites Full-Service Dashboard User Guide is a comprehensive user guide that provides procedural information and field descriptions for all windows in the Waites Full-Service Dashboard.',
      ),
    },
    {
      buttonText: t('Open guide'),
      description: t(
        'The product guides below provide information on how to install and maintain the Waited hardware ',
      ),
      icon: darkMode ? <GuideQuestionDarkIcon /> : <GuideQuestionIcon />,
      onClick: () => {
        setActiveModal({ name: MODAL_NAMES.productGuidesModal });
      },
      title: t('Product Guides'),
    },
    {
      buttonText: t('Open guide'),
      onClick: () => {
        const newWindow = window.open(
          (data?.sensors_guide?.[profileData?.user?.language_iso_code] || data?.sensors_guide?.['en'])?.url ||
            defaultUrl,
          '_blank',
        );

        if (newWindow) {
          newWindow.focus();
        }
      },
      icon: darkMode ? <GuideDarkIcon /> : <GuideIcon />,
      title: t('Waites Sensor Removal and Reinstallation'),
      description: t(
        'A document that includes instructions on how to remove and reinstall sensor motes and tethered sensors.',
      ),
    },
  ];

  const supportResources: Array<IOther> = [
    {
      buttonText: t('Start Onboarding'),
      onClick: () => {
        navigate('/');
        relaunchOnboarding();
      },
      icon: darkMode ? <OnboardingDarkIcon /> : <OnboardingIcon />,
      title: t('New user overview'),
      description: t(
        'Review a short overview of key dashboard features to get started (or refresh your memory).',
      ),
    },
    {
      buttonText: t('Submit Warranty Claim'),
      onClick: () => {
        setActiveModal({ name: MODAL_NAMES.warrantyModal });
      },
      icon: darkMode ? <WarrantyDarkIcon /> : <WarrantyIcon />,
      title: t('Waites Warranty Claim Form'),
      description: t(
        'We understand sometimes defects happen and we would love to help! The following form must be filled before the issue can be resolved. Thank you for your patience as we promptly address your claim. ',
      ),
    },
  ];

  return (
    <>
      <Header title={t('Help and Resources')} showLogo={true} />
      <div className={style.helpPageBody}>
        <h4 className={style.sectionTitle}>{t('Apps')}</h4>
        <div className={style.sectionApps}>
          {apps.map((app) => (
            <AppCard key={uuidv4()} app={app} />
          ))}
        </div>
        <h4 className={style.sectionTitle}>{t('Others')}</h4>
        <div className={style.sectionOthers}>
          {others.map((other) => (
            <OtherCard key={uuidv4()} other={other} />
          ))}
        </div>
        <h4 className={style.sectionTitle}>{t('Support Resources')}</h4>
        <div className={style.sectionSupportResources}>
          {supportResources?.map((resources) => <OtherCard key={uuidv4()} other={resources} />)}
        </div>
        <h4 className={style.sectionTitle}>{t('Support Team Information')}</h4>
        <div className={style.sectionTeam}>
          <div className={style.questionCard}>
            <div className={style.questionCardInfo}>
              <h5 className={style.questionCardTitle}>{t('Still have questions?')}</h5>
              <p className={style.questionCardDesc}>
                {t(
                  "If you can't find what you're looking for or have any further questions, our team is always here to help. Complete the form below to send a message to the Waites Support Team.",
                )}
              </p>
            </div>
            <div className={style.questionCardFooter}>
              <Button
                onClick={() => setActiveModal({ name: MODAL_NAMES.contactSupportModal })}
                text={t('Contact support')}
                type="secondary"
              />
            </div>
          </div>
          {supportTeamData?.map((contact) => (
            <TeamMemberCard
              key={contact.id}
              contactName={contact.fullName}
              contactRole={contact.role}
              photoUrl={contact.profileImageUrl}
            />
          ))}
        </div>
      </div>
    </>
  );
};

export default HelpAndResources;
